import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../interfaces/api-response.interface';

export interface EmployerLoginResponse {
    sessionId?: string;
    trxId?: string;
    phone?: string;
    email?: string;
    policies?: any[];
    authCode?: string;
    selectedRole?: string;
    username?: string;
}

export interface UserProfile {
    employName?: string | null;
    sub?: string;
    emailVerified?: boolean;
    memberNumber?: string | null;
    policyNumber?: string | null;
    username?: string;
    userType?: string | null;
    clientNumber?: string | null;
    subNumber?: string | null;
    selectedRole?: string | null;
}

export interface EmployerOTPResponse {
    authCode?: string;
    selectedRole?: string;
    userProfile?: UserProfile;
}

export interface SignupRequest {
    policyNumber: string;
    email: string;
    password: string;
    confirmPassword: string;
    employType: string;
}

export interface SignupResponse {
    message?: string;
    userId?: string;
}

@Injectable({
    providedIn: 'root'
})
export class EmployerAuthService {
    private readonly employerAuthPath = environment.EMPLOYER_AUTH;
    constructor(private http: HttpClient) { }

    private getHeaders(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'withCredentials': 'true',
        });
    }

    /**
     * Register new employer
     */
    signup(signupData: SignupRequest): Observable<ApiResponse<SignupResponse>> {
        const url = `${this.employerAuthPath.baseUrl}${this.employerAuthPath.signupPath}`;
        return this.http.post<ApiResponse<SignupResponse>>(url, signupData, {
            headers: this.getHeaders()
        });
    }

    /**
     * Validate employer credentials
     */
    validateCredentials(username: string, password: string): Observable<ApiResponse<EmployerLoginResponse>> {
        const url = `${this.employerAuthPath.baseUrl}${this.employerAuthPath.loginPath}/validate-credentials`;
        const body = {
            username,
            password,
            portalId: 'employer'
        };
        return this.http.post<ApiResponse<EmployerLoginResponse>>(url, body, {
            headers: this.getHeaders(),
        });
    }

    /**
     * Submit OTP for verification
     * @param sessionId
     * @param otp
     * @param trxId
     * @param isSignup Set to true when verifying OTP for signup flow
     */
    submitOTP(sessionId: string, otp: string, trxId?: string, isSignup: boolean = false): Observable<ApiResponse<EmployerOTPResponse>> {
        const path = isSignup ? this.employerAuthPath.signupPath : this.employerAuthPath.loginPath;
        const url = `${this.employerAuthPath.baseUrl}${path}/submit-otp`;
        const body = {
            sessionId,
            otp,
            trxId
        };
        return this.http.post<ApiResponse<EmployerOTPResponse>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Resend OTP
     */
    resendOTP(sessionId: string): Observable<ApiResponse> {
        const url = `${this.employerAuthPath.baseUrl}${this.employerAuthPath.loginPath}/resend-otp`;
        const body = {
            sessionId
        };
        return this.http.post<ApiResponse>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Initiate forgot password (check user exist)
     */
    initiateForgotPassword(email: string): Observable<ApiResponse<EmployerLoginResponse>> {
        const url = `${this.employerAuthPath.baseUrl}${this.employerAuthPath.forgotPasswordPath}/initiate`;
        const body = {
            username: email
        };
        return this.http.post<ApiResponse<EmployerLoginResponse>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Submit OTP for forgot password
     */
    verifyForgotPasswordOTP(sessionId: string, otp: string): Observable<ApiResponse<EmployerOTPResponse>> {
        const url = `${this.employerAuthPath.baseUrl}${this.employerAuthPath.forgotPasswordPath}/verify-otp`;
        const body = {
            sessionId,
            otp
        };
        return this.http.post<ApiResponse<EmployerOTPResponse>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Reset password after OTP verification
     */
    resetForgotPassword(sessionId: string, password: string, confirmPassword: string): Observable<ApiResponse<any>> {
        const url = `${this.employerAuthPath.baseUrl}${this.employerAuthPath.forgotPasswordPath}/reset-password`;
        const body = {
            sessionId,
            password,
            confirmPassword
        };
        return this.http.post<ApiResponse<any>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Resend OTP for forgot password
     */
    resendForgotPasswordOTP(sessionId: string): Observable<ApiResponse<any>> {
        const url = `${this.employerAuthPath.baseUrl}${this.employerAuthPath.forgotPasswordPath}/resend-otp`;
        const body = {
            sessionId
        };
        return this.http.post<ApiResponse<any>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Change password after reset verification
     */
    changePassword(sessionId: string, password: string, confirmPassword: string): Observable<ApiResponse<any>> {
        const url = `${this.employerAuthPath.baseUrl}${this.employerAuthPath.forgotPasswordPath}/reset-password`;
        const body = {
            sessionId,
            password,
            confirmPassword
        };
        return this.http.post<ApiResponse<any>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Validate employer signup fields (new flow)
     */
    validateSignup(signupData: SignupRequest): Observable<ApiResponse<any>> {
        const url = `${this.employerAuthPath.baseUrl}/customidp/employee/signup`;
        return this.http.post<ApiResponse<any>>(url, signupData, {
            headers: this.getHeaders()
        });
    }

    /**
     * Accept terms for employer signup
     */
    acceptTerms(payload: { sessionId: string; termsAccepted: boolean; privacyAccepted: boolean; locationAccepted: boolean; contactAccepted: boolean }): Observable<ApiResponse<any>> {
        const url = `${this.employerAuthPath.baseUrl}/customidp/employee/signup/accept-terms`;
        return this.http.post<ApiResponse<any>>(url, payload, {
            headers: this.getHeaders()
        });
    }
} 